import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCurrentTeam } from '@/features/team/current-team'
import { useSubscription } from '@/features/settings/queries'
import { trialState } from '@/features/settings/plans'
import { cn } from '@/lib/utils'

// The last days of a trial, when the banner turns from a note into a warning
const URGENT_DAYS = 3

const seenKey = (teamId: number | undefined) => `trialEndedSeen:${teamId ?? ''}`

function seen(teamId: number | undefined) {
  try {
    return localStorage.getItem(seenKey(teamId)) === '1'
  } catch {
    return false
  }
}

/**
 * The Premium trial a new team starts on: when it ends while it runs, and
 * once that it has ended (until closed). Only admins get the button, since
 * only they can buy.
 */
export function TrialBanner() {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const { current, isAdmin } = useCurrentTeam()
  const { data: sub } = useSubscription()
  const teamId = current?.team?.id ?? current?.team?.Id
  const [closed, setClosed] = useState(() => seen(teamId))

  const trial = trialState(sub)
  // the subscription page says all of it already
  if (trial.state === 'none' || pathname.startsWith('/settings/subscription')) return null
  if (trial.state === 'ended' && (closed || seen(teamId))) return null

  const date = trial.ends.toLocaleDateString(i18n.resolvedLanguage, { dateStyle: 'long' })
  const urgent = trial.state === 'ended' || trial.daysLeft <= URGENT_DAYS
  const close = () => {
    try {
      localStorage.setItem(seenKey(teamId), '1')
    } catch {
      /* the banner just comes back next time */
    }
    setClosed(true)
  }

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2.5 text-sm md:px-6',
        urgent ? 'border-window/40 bg-window/15' : 'border-primary/20 bg-primary/8',
      )}
    >
      <Sparkles className={cn('size-4 shrink-0', urgent ? 'text-foreground' : 'text-primary')} aria-hidden />
      <p className="min-w-0 flex-1">
        {trial.state === 'active' ? (
          <>
            <span className="font-medium">{t('v2.trial.endsOn', { date })}</span>{' '}
            <span className="text-muted-foreground">{t('v2.trial.daysLeft', { count: trial.daysLeft })}</span>
          </>
        ) : (
          <>
            <span className="font-medium">{t('v2.trial.ended')}</span> <span className="text-muted-foreground">{t('v2.trial.endedBody')}</span>
          </>
        )}
      </p>
      {isAdmin && (
        <Button size="sm" variant={urgent ? 'default' : 'outline'} asChild>
          <Link to="/settings/subscription?upgrade">{t('v2.trial.choosePlan')}</Link>
        </Button>
      )}
      {trial.state === 'ended' && (
        <Button size="icon" variant="ghost" className="size-7" onClick={close} aria-label={t('v2.trial.close')}>
          <X />
        </Button>
      )}
    </div>
  )
}
